import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { TextField } from "@mui/material";
import { onlyNumber, onlyNumberdesimal } from "../../helpers/formats";
import {
  addIncomeService,
  updateIncomeService,
} from "../../services/incomeServices";
import CustomField from "../../components/common/customField";
import { initialValuesForIncome } from "../../components/common/utlity/intialValues";
import {
  optionsForExpenseTax,
  optionsForIncomeCategory,
  optionsForIncomeType,
} from "../../components/common/utlity/options";
import BeatLoader from "react-spinners/BeatLoader";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import TourGuide from "../../components/common/TourGuide";
import { IncomeSteps } from "../../lib/constants/TourSteps";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiDollarSign, FiEdit3 } from "react-icons/fi";

const AddIncome = ({
  close,
  updateExpense,
  addExpense,
  item,
  businessUuid,
}) => {
  const navigate = useNavigate();
  const [totalAmounts, setTotalAmounts] = useState(0);
  const [incomeIsLoading, setIncomeIsLoading] = useState(false);
  const { t } = useTranslation();
  const { bussinessUuid: paramUuid } = useParams();
  const finalBusinessUuid = businessUuid || paramUuid;
  const income = useSelector((state) => state?.income.income);
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    const waitForElements = () => {
      const allElementsExist = document.querySelector(".income-taxable-tour");
      //  document.querySelector(".transaction-status-tour");

      if (income?.length === 0 && allElementsExist) {
        setRunTour(true);
      } else {
        setTimeout(waitForElements, 300);
      }
    };

    waitForElements();
  }, [income]);
  const signInSchema = Yup.object().shape({
    incomeName: Yup.string().required(
      <span className='text-xs dark:text-red-500'>Income name is required</span>
    ),
    amount: Yup.number().required(
      <span className='text-xs dark:text-red-500'>Amount is required</span>
    ),
  });

  const onSubmit = async (values, { resetForm }) => {
    if (!finalBusinessUuid) {
      toast.error("Business UUID is missing");
      return;
    }

    const { totalAmount, ...rem } = values;
    const dataForCreate = {
      totalAmount: totalAmounts, // Fixed: using the correct variable (was totalAmounts)
      ...rem,
      bussinessUuid: finalBusinessUuid, // Ensure UUID is included in payload
    };

    try {
      setIncomeIsLoading(true);

      if (item) {
        // Update existing income
        const { incomeUuid } = item;
        const result = await updateIncomeService(
          finalBusinessUuid, // Using finalBusinessUuid instead of bussinessUuid
          incomeUuid,
          dataForCreate
        );

        if (result.data) {
          updateExpense(result?.data);
          toast.success(t("Income Updated Successfully")); // Using translation
          navigate(-1)
          close(true);
        } else {
          throw new Error("Something Went  wrong!");
        }
      } else {
        // Create new income
        const result = await addIncomeService(finalBusinessUuid, dataForCreate);

        if (result.data) {
          addExpense(result.data);
          toast.success(t("Income Added Successfully")); // Using translation
          resetForm();
          navigate(-1)
          close();
        } else {
          throw new Error("Failed to Add Income");
        }
      }
    } catch (error) {
      toast.error(t("Something Went  wrong!")); // Using translation
      console.error("Error:", error);
    } finally {
      setIncomeIsLoading(false);
    }
  };

  return (
    <>
      <TourGuide steps={IncomeSteps} runTour={runTour} />
      <Formik
        // enableReinitialize={true}
        initialValues={initialValuesForIncome(item ? item : null)}
        validationSchema={signInSchema}
        onSubmit={onSubmit}
      >
        {(formik) => {
          const {
            errors,
            values,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
          } = formik;
          const calculateTotalAmount = () => {
            const baseAmount = parseFloat(values.amount) || 0;
            const igst = parseFloat(values.igst) || 0;
            const cgst = parseFloat(values.cgst) || 0;
            const sgst = parseFloat(values.sgst_utgst) || 0;
            const cess = parseFloat(values.cess) || 0;
            const totalGSTPercent = igst + cgst + sgst + cess;
            const totalAmount =
              baseAmount + (baseAmount * totalGSTPercent) / 100;
            setTotalAmounts(totalAmount.toFixed(2));
            return totalAmount.toFixed(2);
          };
          const handleChangeForTaxable = (e) => {
            handleChange(e);
            if (e.target.value === "no_tax") {
              setFieldValue("igst", "");
              setFieldValue("cgst", "");
              setFieldValue("sgst", "");
              setFieldValue("cess", "");
            }
          };
          return (
            <Form>
              <div className='container mx-auto'>
                <div className='grid gap-4 grid-cols-1'>
                  <div className='bg-white dark:bg-slate-900 h-[calc(100vh-64px)] dark:text-white p-2'>
                    <div className="mt-1 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gradient-to-br dark:from-slate-900 dark:via-indigo-950 dark:to-slate-800 shadow-[0_0_15px_rgba(0,0,0,0.08)] dark:shadow-[0_0_20px_rgba(0,0,0,0.4)]">
                      <div className="mb-5 flex items-center justify-between">
  <div className="flex items-center gap-4">
    <button
      type="button"
      onClick={() => navigate(-1)}
      className="flex items-center justify-center w-11 h-11 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 transition-all shadow-sm"
    >
      <FiArrowLeft className="text-xl text-gray-700 dark:text-white" />
    </button>

    <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shadow">
      {item ? <FiEdit3 size={28} /> : <FiDollarSign size={28} />}
    </div>

    <div>
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
        {item ? t('EXTRA.UPDATE_INCOME') : t('INCOME.ADD_INCOME')}
      </h1>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        {item
          ? t('EXTRA.MODIFY_THE_INCOME')
          : t('EXTRA.ENTER_THE_INCOME')}
      </p>
    </div>
  </div>
</div>
                      <div className='grid grid-cols-3 gap-3 mb-3'>
                      <div className='p-1 mt-3 col-span-1'>
                        <CustomField
                          type='text'
                          name='incomeName'
                          label={t("FORM.INCOME_NAME")}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          value={values.incomeName}
                          error={errors.incomeName}
                          touched={touched.incomeName}
                        />
                      </div>
                      {/* incomeType */}
                      <div className='p-1 mt-3 col-span-1'>
                        <CustomField
                          type='select'
                          name='incomeType'
                          label={t("FORM.INCOME_TYPE")}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          options={optionsForIncomeType}
                          setFieldValue={setFieldValue}
                          value={values.incomeType}
                        />
                      </div>
                      <div className='p-1 mt-3 mb-2 w-full'>
                        <CustomField
                          type='select'
                          name='incomeCategory'
                          label={t("FORM.INCOME_CATEGORY")}
                          placeholder='Select Category'
                          options={optionsForIncomeCategory}
                          setFieldValue={(name, value) => {
                            setFieldValue(name, value);
                            if (value !== "other") {
                              setFieldValue("incomeOtherCategory", ""); // Clear custom category
                            }
                          }}
                          handleChange={(e) => {
                            const selectedValue = e.target.value;
                            setFieldValue("incomeCategory", selectedValue); // Ensure category is set
                            if (selectedValue !== "other") {
                              setFieldValue("incomeOtherCategory", ""); // Clear custom input
                            }
                          }}
                          value={values.incomeCategory} // Ensure correct value is passed
                          error={errors.incomeCategory}
                          touched={touched.incomeCategory}
                        />
                      </div>

                      {/* Display "Other" Input if 'Other' is selected */}
                      {values.incomeCategory === "other" && (
                        <div className='p-1 mb-2 w-full'>
                          <TextField
                            type='text'
                            name='incomeOtherCategory'
                            label='Income Category *'
                            // placeholder='Enter Income Category'
                            onChange={(e) => {
                              handleChange(e);
                              setFieldValue(
                                "incomeOtherCategory",
                                e.target.value
                              );
                              setFieldValue("incomeCategory", "other"); // Keep "other" selected
                            }}
                            onBlur={handleBlur}
                            value={values.incomeOtherCategory} // Ensure input value is displayed
                            error={
                              values.incomeCategory === "other" &&
                              values.incomeOtherCategory === ""
                            }
                            touched={touched.incomeOtherCategory}
                            helperText={
                              values.incomeCategory === "other" &&
                              values.incomeOtherCategory === ""
                                ? "Other Category Is Required"
                                : ""
                            }
                            size='small'
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 30,
                              },
                            }}
                            InputProps={{
                              className:
                                "dark:text-white dark:bg-[#1f2937] dark:border-white focus:border-blue-600",
                            }}
                            InputLabelProps={{
                              className: "dark:text-white",
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className='grid grid-cols-3 gap-3 mb-3'>
                      <div className='col-span-1 p-1'>
                        <CustomField
                          type='text'
                          name='reference'
                          label={t("FORM.REFERENCE")}
                          placeholder='Reference'
                          handleChange={handleChange}
                          value={values.reference}
                        />
                      </div>
                      <div className='col-span-1 p-1'>
                        <CustomField
                          type='text'
                          name='amount'
                          label={t("FORM.AMOUNT*")}
                          placeholder='Amount'
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          onKeyPress={onlyNumberdesimal}
                          value={values.amount}
                          error={errors.amount}
                          touched={touched.amount}
                        />
                      </div>

                      {/* Date */}
                      <div className='p-1'>
                        <CustomField
                          type='date'
                          name='date'
                          label={t("FORM.CREATOR_DATE")}
                          placeholder='Create Date'
                          handleChange={handleChange}
                          // handleBlur={handleBlur}
                          value={values.date}
                          // error={errors.date}
                          // touched={touched.date}
                        />
                      </div>
                    </div>
                    <div className="mt-1 mb-3 grid grid-cols-3 gap-3">
                      <div className='p-1 income-taxable-tour'>
                        <CustomField
                          type='select'
                          name='taxableIncome'
                          label={t("FORM.TAXABLE")}
                          placeholder='Taxable'
                          options={optionsForExpenseTax}
                          setFieldValue={setFieldValue}
                          handleChange={handleChangeForTaxable}
                          value={values.taxableIncome}
                        />
                      </div>
                      {values.taxableIncome === "taxable" && (
                        <>
                          <div className='p-1'>
                            <CustomField
                              type='text'
                              name='igst'
                              label={t("TAX.IGST")}
                              placeholder='IGST'
                              handleChange={
                                values.sgst_utgst || values.cgst
                                  ? undefined
                                  : handleChange
                              }
                              handleBlur={handleBlur}
                              value={values.igst}
                              onKeyPress={onlyNumber}
                              helperText={
                                values.sgst_utgst || values.cgst
                                  ? "Cannot calculate with SGST or CGST"
                                  : ""
                              }
                              disabled={Number(values.cgst) > 0 || Number(values.sgst_utgst) > 0}
                              // disabled={values.cgst || values.sgst_utgst}
                            />
                          </div>
                          <div className='p-1'>
                            <CustomField
                              type='text'
                              name='cgst'
                              label={t("TAX.CGST")}
                              placeholder='CGST'
                              handleChange={
                                values.igst ? undefined : handleChange
                              }
                              handleBlur={handleBlur}
                              value={values.cgst}
                              onKeyPress={onlyNumber}
                              helperText={
                                values.igst ? "Cannot calculate with IGST" : ""
                              }
                              disabled={values.igst}
                            />
                          </div>
                          <div className='p-1'>
                            <CustomField
                              type='text'
                              name='sgst_utgst'
                              label={t("TAX.SGST")}
                              placeholder='SGST/UTGST'
                              handleChange={
                                values.igst ? undefined : handleChange
                              }
                              handleBlur={handleBlur}
                              value={values.sgst_utgst}
                              onKeyPress={onlyNumber}
                              // maxLength={3}
                              helperText={
                                values.igst ? "Cannot calculate with IGST" : ""
                              }
                              disabled={values.igst}
                            />
                          </div>

                          <div className='p-1'>
                            <CustomField
                              type='text'
                              name='cess'
                              label={t("TAX.CESS")}
                              placeholder='CESS(%)'
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              value={values.cess}
                              onKeyPress={onlyNumber}
                            />
                          </div>
                          <div className="col-span-1 p-1">
                            <CustomField
                              type='text'
                              name='totalAmount'
                              label={t("COMMON.TOTAL_AMOUNT")}
                              placeholder='Total Amount'
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              value={calculateTotalAmount()}
                              onKeyPress={onlyNumber}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    <div className='grid grid-cols-2 gap-2 mb-4 mt-5 '>
                      <div className='col-span-2'>
                        <TextField
                          fullWidth
                          id='description'
                          name='description'
                          label={t("FORM.DESCRIPTION")}
                          placeholder='Enter Income Description'
                          onChange={handleChange}
                          multiline
                          rows={2}
                          value={values.description}
                          InputProps={{
                            className:
                              "dark:text-white dark:bg-[#1f2937] dark:border-white focus:border-blue-600",
                          }}
                          InputLabelProps={{
                            className: "dark:text-white",
                          }}
                        />
                      </div>
                    </div>
                    <div className='flex justify-end'>
                      {item ? (
                      
                           <button
                                                  type='submit'
                                                  className='w-1/2 py-2 px-2 bg-blue-600 hover:bg-blue-700 text-white  mt-4 font-bold rounded flex items-center justify-center'
                                                  disabled={incomeIsLoading}
                                                >
                                                  {incomeIsLoading ? (
                                                    <BeatLoader
                                                      size={8}
                                                      color='#ffffff'
                                                      loading={incomeIsLoading}
                                                    />
                                                  ) : (
                                                    t("FORM.UPDATE_INCOME")
                                                  )}
                                                </button>
                      ) : (
                        <button
                          type='submit'
                          className='w-1/2 py-2 px-2 bg-blue-600 hover:bg-blue-700 text-white  mt-4 font-bold rounded justify-content-right'
                          disabled={incomeIsLoading} // Disable button during loading
                        >
                          {incomeIsLoading ? (
                            <BeatLoader
                              size={8}
                              color='#ffffff'
                              loading={incomeIsLoading}
                            />
                          ) : (
                            t("INCOME.ADD_INCOME")
                          )}
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
export default AddIncome;
